import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddTenantApprovalMatrix.css";
import { API_BASE } from "../../../ApiUrl";
import SettingsTabs from "../Settings/SettingsTabs";

const API_URL = `${API_BASE}/tenant-approval-matrices/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;
const PROJECTS_API_URL = `${API_BASE}/projects/`;
const ROLES_API_URL = `${API_BASE}/roles/`;

const DOCUMENT_TYPE_OPTIONS = [
  { value: "PROJECT", label: "Project" },
  { value: "REPORT", label: "Report" }
];

const emptyMatrix = () => ({
  id: null,
  project: "",
  document_type: "",
  sequence: []
});

const AddTenantApprovalMatrix = () => {
  const navigate = useNavigate();

  const [matrices, setMatrices] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState([emptyMatrix()]);
  const [tenantId, setTenantId] = useState(null);

  const [projectsList, setProjectsList] = useState([]);
  const [rolesList, setRolesList] = useState([]);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  useEffect(() => {
    const tenantUser = getTenantUser();
    if (tenantUser?.tenant_id) setTenantId(Number(tenantUser.tenant_id));
  }, []);

  useEffect(() => {
    if (tenantId) fetchMatrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchMatrices = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        TENANT_RECORDS_API_URL,
        {
          tenant_id: tenantId,
          tables: ["tenant_approval_matrices"]
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data;

      let rows = [];
      if (result?.data?.tenant_approval_matrices) {
        rows = result.data.tenant_approval_matrices;
      } else if (result?.tenant_approval_matrices) {
        rows = result.tenant_approval_matrices;
      } else if (Array.isArray(result?.data)) {
        rows = result.data;
      } else if (Array.isArray(result)) {
        rows = result;
      }

      setMatrices(rows);
    } catch (error) {
      console.error("Error fetching approval matrices:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to load matrices",
        text:
          error.response?.data?.detail ||
          "Something went wrong while loading matrices."
      });
    } finally {
      setLoading(false);
    }
  };

  // ---- Lookups ----
  useEffect(() => {
    const fetchLookups = async () => {
      const tenantUser = getTenantUser();
      try {
        const [projectsRes, rolesRes] = await Promise.all([
          axios.get(PROJECTS_API_URL, {
            params: tenantUser?.tenant_id
              ? { tenant: tenantUser.tenant_id }
              : undefined
          }),
          axios.get(ROLES_API_URL)
        ]);

        const projectsData = projectsRes.data?.data
          ? projectsRes.data.data
          : Array.isArray(projectsRes.data)
          ? projectsRes.data
          : projectsRes.data?.results || [];

        const rolesData = rolesRes.data?.data
          ? rolesRes.data.data
          : Array.isArray(rolesRes.data)
          ? rolesRes.data
          : rolesRes.data?.results || [];

        setProjectsList(projectsData);
        setRolesList(rolesData);
      } catch (error) {
        console.error("Error fetching lookups:", error);
      }
    };

    fetchLookups();
  }, []);

  // ---- Form handlers ----
  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setFormData((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [name]: value } : m))
    );
  };

  const addRoleToSequence = (matrixIndex, roleCode) => {
    if (!roleCode) return;
    setFormData((prev) =>
      prev.map((m, i) => {
        if (i !== matrixIndex) return m;
        if (m.sequence.includes(roleCode)) return m;
        return { ...m, sequence: [...m.sequence, roleCode] };
      })
    );
  };

  const removeRoleFromSequence = (matrixIndex, roleCode) => {
    setFormData((prev) =>
      prev.map((m, i) =>
        i === matrixIndex
          ? { ...m, sequence: m.sequence.filter((c) => c !== roleCode) }
          : m
      )
    );
  };

  const moveRole = (matrixIndex, fromIdx, toIdx) => {
    setFormData((prev) =>
      prev.map((m, i) => {
        if (i !== matrixIndex) return m;
        if (toIdx < 0 || toIdx >= m.sequence.length) return m;
        const next = [...m.sequence];
        const [item] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, item);
        return { ...m, sequence: next };
      })
    );
  };

  // ---- Row handlers ----
  const handleAdd = () => {
    setFormData([emptyMatrix()]);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  const handleAddAnother = () => {
    setFormData((prev) => [...prev, emptyMatrix()]);
  };

  const handleRemoveForm = (index) => {
    setFormData((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (m) => {
    setFormData([
      {
        id: m.id,
        project: m.project ? String(m.project) : "",
        document_type: m.document_type || "",
        sequence: Array.isArray(m.sequence)
          ? [...m.sequence]
          : typeof m.sequence === "string" && m.sequence
          ? m.sequence.split(",").map((s) => s.trim()).filter(Boolean)
          : []
      }
    ]);
    setEditingId(m.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (m) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete Matrix?",
      text: `Are you sure you want to delete the matrix for "${
        m.document_type || "this document type"
      }"?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626"
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_URL}${m.id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Approval matrix deleted successfully.",
        timer: 1500,
        showConfirmButton: false
      });

      fetchMatrices();
    } catch (error) {
      console.error("Error deleting approval matrix:", error);
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          "Unable to delete the matrix."
      });
    }
  };

  const validateRow = (m, index) => {
    const label = `Matrix ${index + 1}`;

    if (!m.document_type) {
      Swal.fire({
        icon: "warning",
        title: "Document Type Required",
        text: `Please select a Document Type for ${label}.`
      });
      return false;
    }

    if (!Array.isArray(m.sequence) || m.sequence.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Roles Selected",
        text: `Please add at least one role to the approval sequence for ${label}.`
      });
      return false;
    }

    return true;
  };

  const buildPayload = (m) => ({
    project: m.project ? Number(m.project) : null,
    document_type: m.document_type,
    sequence: m.sequence,
    tenant: tenantId
  });

  const extractError = (error) => {
    if (error.response?.data) {
      const d = error.response.data;
      if (typeof d === "string") return d;
      if (d.detail) return d.detail;
      if (d.message) return d.message;
      if (d.errors) {
        return Object.entries(d.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${Array.isArray(errors) ? errors.join(", ") : errors}`
          )
          .join(" | ");
      }
      return JSON.stringify(d);
    }
    return error.message || "Unknown error";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({
        icon: "error",
        title: "Tenant ID missing",
        text: "Unable to identify the current tenant."
      });
      return;
    }

    for (let i = 0; i < formData.length; i++) {
      if (!validateRow(formData[i], i)) return;
    }

    const activeRows = formData.filter(
      (m) => m.document_type || m.sequence.length || m.id
    );

    const keyOf = (m) =>
      `${m.project || "default"}::${m.document_type}`.toLowerCase();
    const keys = activeRows.map(keyOf);
    const duplicateIndex = keys.findIndex((k, i) => keys.indexOf(k) !== i);

    if (duplicateIndex !== -1) {
      Swal.fire({
        icon: "warning",
        title: "Duplicate Matrix",
        text: "The same project + document type is configured more than once."
      });
      return;
    }

    const CONFIG = {
      headers: { "Content-Type": "application/json" }
    };

    try {
      setLoading(true);

      if (isEditing) {
        const payload = buildPayload(formData[0]);
        await axios.put(`${API_URL}${editingId}/`, payload, CONFIG);

        await Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        for (const m of activeRows) {
          await axios.post(API_URL, buildPayload(m), CONFIG);
        }

        await Swal.fire({
          icon: "success",
          title: "Created Successfully",
          text: `${activeRows.length} matrix${
            activeRows.length > 1 ? "es" : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([emptyMatrix()]);

      await fetchMatrices();
    } catch (error) {
      console.error("Error saving approval matrix:", error);
      Swal.fire({
        icon: "error",
        title: isEditing ? "Update failed" : "Creation failed",
        text: extractError(error)
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([emptyMatrix()]);
  };

  // ---- Lookup helpers ----
  const roleLabel = (code) => {
    const found = rolesList.find(
      (r) =>
        r.code === code ||
        r.role_code === code ||
        r.name === code ||
        r.role_name === code
    );
    return found
      ? found.name || found.role_name || found.code || code
      : code;
  };

  const getProjectName = (id) => {
    if (!id) return "Tenant-wide default";
    const p = projectsList.find((x) => String(x.id) === String(id));
    return p?.name || p?.project_name || `Project ${id}`;
  };

  const getDocumentTypeLabel = (type) => {
    const found = DOCUMENT_TYPE_OPTIONS.find((o) => o.value === type);
    return found?.label || type || "-";
  };

  const getSequenceArray = (m) => {
    if (Array.isArray(m.sequence)) return m.sequence;
    if (typeof m.sequence === "string" && m.sequence) {
      try {
        const parsed = JSON.parse(m.sequence);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        return m.sequence.split(",").map((s) => s.trim()).filter(Boolean);
      }
    }
    return [];
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="approval-matrix-page">
          <SettingsTabs />

          {!showForm ? (
            <>
              {/* ==================================================
                  LIST HEADER
              ================================================== */}

              <div className="approval-matrix-page-header">
                <div>
                  <h2>Approval Matrix</h2>
                </div>

                <button
                  type="button"
                  className="approval-matrix-add-top-btn"
                  onClick={handleAdd}
                >
                  + Add Matrix
                </button>
              </div>

              {/* ==================================================
                  TABLE
              ================================================== */}

              <div className="approval-matrix-list-card">
                {loading ? (
                  <div className="approval-matrix-loading">
                    Loading matrices...
                  </div>
                ) : matrices.length === 0 ? (
                  <div className="approval-matrix-empty">
                    <div className="approval-matrix-empty-icon">+</div>
                    <h3>No Approval Matrices Found</h3>
                    <p>
                      There are no approval matrices available for the current
                      tenant.
                    </p>
                    <button
                      type="button"
                      className="approval-matrix-empty-add-btn"
                      onClick={handleAdd}
                    >
                      + Add Matrix
                    </button>
                  </div>
                ) : (
                  <div className="approval-matrix-table-wrapper">
                    <table className="approval-matrix-table">
                      <thead>
                        <tr>
                          <th>Project</th>
                          <th>Document Type</th>
                          <th>Sequence</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {matrices.map((m) => (
                          <tr key={m.id}>
                            <td>
                              <strong>{getProjectName(m.project)}</strong>
                            </td>

                            <td>
                              <span className="approval-matrix-type-pill">
                                {getDocumentTypeLabel(m.document_type)}
                              </span>
                            </td>

                            <td>
                              <div className="approval-matrix-chip-list">
                                {getSequenceArray(m).length === 0 ? (
                                  <span className="approval-matrix-muted">
                                    -
                                  </span>
                                ) : (
                                  getSequenceArray(m).map((code, i) => (
                                    <span
                                      key={`${code}-${i}`}
                                      className="approval-matrix-order-pill"
                                    >
                                      {i + 1}. {roleLabel(code)}
                                    </span>
                                  ))
                                )}
                              </div>
                            </td>

                            <td>
                              <div className="approval-matrix-actions">
                                <button
                                  type="button"
                                  className="approval-matrix-edit-btn"
                                  onClick={() => handleEdit(m)}
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="approval-matrix-delete-btn"
                                  onClick={() => handleDelete(m)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* ==================================================
                  ADD / EDIT HEADER
              ================================================== */}

              <div className="approval-matrix-page-header">
                <div>
                  <h2>
                    {isEditing ? "Edit Approval Matrix" : "Create Matrices"}
                  </h2>
                </div>

                <button
                  type="button"
                  className="approval-matrix-back-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              {/* ==================================================
                  FORM
              ================================================== */}

              <form onSubmit={handleSubmit}>
                {formData.map((m, index) => (
                  <div
                    className="approval-matrix-card"
                    key={index}
                    style={{ marginBottom: "20px" }}
                  >
                    <div className="approval-matrix-form-body">
                      <div className="approval-matrix-record-header">
                        <h4>Matrix {index + 1}</h4>

                        {!isEditing && formData.length > 1 && (
                          <button
                            type="button"
                            className="approval-matrix-remove-btn"
                            onClick={() => handleRemoveForm(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="approval-matrix-form-grid">
                        <div className="approval-matrix-form-group">
                          <label>Project</label>
                          <select
                            name="project"
                            value={m.project}
                            onChange={(e) => handleChange(index, e)}
                          >
                            <option value="">
                              Tenant-wide default (all projects)
                            </option>
                            {projectsList.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name || p.project_name || `Project ${p.id}`}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="approval-matrix-form-group">
                          <label>
                            Document Type <span>*</span>
                          </label>
                          <select
                            name="document_type"
                            value={m.document_type}
                            onChange={(e) => handleChange(index, e)}
                          >
                            <option value="">Select document type</option>
                            {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="approval-matrix-form-group approval-matrix-form-group--full">
                          <label>
                            Approval Sequence <span>*</span>
                          </label>

                          <div className="approval-matrix-sequence">
                            {m.sequence.length === 0 ? (
                              <span className="approval-matrix-sequence-empty">
                                No roles selected yet
                              </span>
                            ) : (
                              m.sequence.map((code, seqIdx) => (
                                <span
                                  className="approval-matrix-chip"
                                  key={`${code}-${seqIdx}`}
                                >
                                  <button
                                    type="button"
                                    className="approval-matrix-chip-btn"
                                    onClick={() =>
                                      moveRole(index, seqIdx, seqIdx - 1)
                                    }
                                    disabled={seqIdx === 0}
                                    title="Move up"
                                  >
                                    ↑
                                  </button>
                                  <span className="approval-matrix-chip-label">
                                    {seqIdx + 1}. {roleLabel(code)}
                                  </span>
                                  <button
                                    type="button"
                                    className="approval-matrix-chip-btn"
                                    onClick={() =>
                                      moveRole(index, seqIdx, seqIdx + 1)
                                    }
                                    disabled={
                                      seqIdx === m.sequence.length - 1
                                    }
                                    title="Move down"
                                  >
                                    ↓
                                  </button>
                                  <button
                                    type="button"
                                    className="approval-matrix-chip-remove"
                                    onClick={() =>
                                      removeRoleFromSequence(index, code)
                                    }
                                    title="Remove"
                                  >
                                    ×
                                  </button>
                                </span>
                              ))
                            )}
                          </div>

                          <div className="approval-matrix-role-picker">
                            <select
                              value=""
                              onChange={(e) => {
                                addRoleToSequence(index, e.target.value);
                                e.target.value = "";
                              }}
                            >
                              <option value="">Add role to sequence…</option>
                              {rolesList
                                .filter(
                                  (r) =>
                                    !m.sequence.includes(
                                      r.code || r.role_code || r.name
                                    )
                                )
                                .map((r) => {
                                  const code =
                                    r.code || r.role_code || r.name;
                                  return (
                                    <option key={r.id} value={code}>
                                      {r.name ||
                                        r.role_name ||
                                        r.code ||
                                        `Role ${r.id}`}
                                    </option>
                                  );
                                })}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditing && (
                  <button
                    type="button"
                    className="approval-matrix-add-btn"
                    onClick={handleAddAnother}
                    style={{ marginBottom: "20px" }}
                  >
                    <span>+</span>
                    Add Another Matrix
                  </button>
                )}

                <div className="approval-matrix-form-actions">
                  <button
                    type="button"
                    className="approval-matrix-cancel-btn"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="approval-matrix-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update Matrix"
                      : "Save Matrices"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddTenantApprovalMatrix;